import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';
const ZOBOX_HOVER = '#1f2660';

function VendorRegistration() {
  const [formData, setFormData] = useState({
    // Company Details
    entity_name: '',
    complete_address: '',
    gst_registered: false,
    gst_number: '',
    company_registration_no: '',
    pan_no: '',
    nature_of_business: '',
    
    // Contact Information
    vendor_name: '',
    contact_person: '',
    telephone: '',
    mobile_phone: '',
    official_email: '',
    
    // Bank Details
    account_holder_name: '',
    bank_branch_name: '',
    account_no: '',
    ifsc_code: ''
  });

  // Document uploads
  const [documents, setDocuments] = useState({
    gst_certificate: null,
    cin_document: null,
    pan_card: null,
    director_aadhar: null,
    director_pan: null,
    cancelled_cheque: null,
    company_seal: null
  });

  const [submitted, setSubmitted] = useState(false);
  const [vrfId, setVrfId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, documentType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should not exceed 5MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, PNG, and PDF files are allowed');
        return;
      }

      setDocuments(prev => ({
        ...prev,
        [documentType]: file
      }));
    }
  };

  const uploadDocuments = async (vrf_id) => {
    const uploadedDocs = [];
    
    for (const [docType, file] of Object.entries(documents)) {
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          setUploadProgress(prev => ({ ...prev, [docType]: 'uploading' }));
          
          // For now, we'll store document references
          // In production, you'd upload to cloud storage
          uploadedDocs.push({
            type: docType,
            filename: file.name,
            size: file.size
          });
          
          setUploadProgress(prev => ({ ...prev, [docType]: 'completed' }));
        } catch (err) {
          console.error(`Error uploading ${docType}:`, err);
          setUploadProgress(prev => ({ ...prev, [docType]: 'failed' }));
        }
      }
    }
    
    return uploadedDocs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Submit VRF data
      const response = await axios.post(`${API}/api/vendor-registration`, formData);
      const vrf_id = response.data.vrf_id;
      
      // Upload documents
      await uploadDocuments(vrf_id);
      
      setVrfId(vrf_id);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
          <div className="text-6xl mb-6">✅</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Registration Submitted Successfully!</h1>
          <p className="text-gray-600 mb-6">Thank you for registering with Zobox. Your application has been received.</p>
          
          <div style={{ backgroundColor: `${ZOBOX_PRIMARY}15` }} className="rounded-lg p-6 mb-6">
            <p className="text-sm text-gray-700 mb-2">Your Application ID:</p>
            <p className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>{vrfId}</p>
            <p className="text-xs text-gray-600 mt-2">Please save this ID for tracking your application status</p>
          </div>

          <div className="text-sm text-gray-600 mb-6">
            <p>Our team will review your application and contact you within 2-3 business days.</p>
          </div>

          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 text-white rounded-lg font-medium transition"
            style={{ backgroundColor: ZOBOX_PRIMARY }}
            onMouseEnter={(e) => e.target.style.backgroundColor = ZOBOX_HOVER}
            onMouseLeave={(e) => e.target.style.backgroundColor = ZOBOX_PRIMARY}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const documentFields = [
    { key: 'gst_certificate', label: 'Company GST Certificate', required: formData.gst_registered },
    { key: 'cin_document', label: 'CIN Number Document', required: true },
    { key: 'pan_card', label: 'Company PAN Card', required: true },
    { key: 'director_aadhar', label: 'Director Aadhar Card', required: true },
    { key: 'director_pan', label: 'Director PAN Card', required: true },
    { key: 'cancelled_cheque', label: 'Cancelled Cheque', required: true },
    { key: 'company_seal', label: 'Company Authorized Seal & Signature', required: true }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Vendor Registration Form</h1>
          <p className="mt-2 text-gray-600">Please fill in all required information to register as a Zobox vendor</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entity Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="entity_name"
                  value={formData.entity_name}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Complete Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="complete_address"
                  value={formData.complete_address}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="gst_registered"
                  checked={formData.gst_registered}
                  onChange={handleChange}
                  className="w-4 h-4 border-gray-300 rounded"
                  style={{ accentColor: ZOBOX_PRIMARY }}
                />
                <label className="ml-2 text-sm text-gray-700">GST Registered</label>
              </div>

              {formData.gst_registered && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleChange}
                    required={formData.gst_registered}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                    style={{ outlineColor: ZOBOX_PRIMARY }}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Registration No (CIN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company_registration_no"
                  value={formData.company_registration_no}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PAN No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="pan_no"
                  value={formData.pan_no}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nature of Business <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nature_of_business"
                  value={formData.nature_of_business}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Manufacturing, Distribution, Trading"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="vendor_name"
                  value={formData.vendor_name}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telephone</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile_phone"
                  value={formData.mobile_phone}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Official Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="official_email"
                  value={formData.official_email}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Bank Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Holder Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name of Bank Branch</label>
                <input
                  type="text"
                  name="bank_branch_name"
                  value={formData.bank_branch_name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_no"
                  value={formData.account_no}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IFSC Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent"
                  style={{ outlineColor: ZOBOX_PRIMARY }}
                />
              </div>
            </div>
          </div>

          {/* KYC Documents Upload */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">📄 KYC Documents Upload</h2>
            <p className="text-sm text-gray-600 mb-6">Please upload the following documents (PDF, JPEG, or PNG, max 5MB each):</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {documentFields.map((doc) => (
                <div key={doc.key} className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {doc.label} {doc.required && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, doc.key)}
                    required={doc.required}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:text-white file:cursor-pointer"
                    style={{ 
                      '--file-bg': ZOBOX_PRIMARY,
                      '--file-hover-bg': ZOBOX_HOVER
                    }}
                  />
                  {documents[doc.key] && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <span className="mr-2">✓</span>
                      <span className="truncate">{documents[doc.key].name}</span>
                    </div>
                  )}
                  {uploadProgress[doc.key] === 'uploading' && (
                    <div className="mt-2 text-sm" style={{ color: ZOBOX_PRIMARY }}>Uploading...</div>
                  )}
                  {uploadProgress[doc.key] === 'completed' && (
                    <div className="mt-2 text-sm text-green-600">✓ Uploaded</div>
                  )}
                  {uploadProgress[doc.key] === 'failed' && (
                    <div className="mt-2 text-sm text-red-600">✗ Upload failed</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 text-white rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              style={{ backgroundColor: ZOBOX_PRIMARY }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = ZOBOX_HOVER)}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = ZOBOX_PRIMARY)}
            >
              {loading ? 'Submitting...' : 'Submit Registration'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        input[type="file"]::file-selector-button {
          background-color: ${ZOBOX_PRIMARY};
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          margin-right: 1rem;
        }
        input[type="file"]::file-selector-button:hover {
          background-color: ${ZOBOX_HOVER};
        }
      `}</style>
    </div>
  );
}

export default VendorRegistration;
